import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../../App";
import {
  buildRealEstateAccessRequestDoc,
  findPendingRealEstateAccessRequest,
  RE_ACCESS_REQUEST_SOURCE,
} from "../../utils/realEstateAccessRequestHelpers";
import {
  MARK_BEATTIE_CONTACT_EMAIL,
  MARK_BEATTIE_CONTACT_PHONE,
} from "../../utils/realEstatePortalAccess";

function safeStr(value) {
  return String(value ?? "").trim();
}

function requesterDisplayName(profile, authUser) {
  const fromProfile =
    [safeStr(profile?.firstName), safeStr(profile?.lastName)].filter(Boolean).join(" ") ||
    safeStr(profile?.displayName) ||
    safeStr(profile?.preferredName);
  if (fromProfile) return fromProfile;
  return safeStr(authUser?.displayName) || "";
}

/**
 * CL-380G / CL-381A — Real estate portal gate with in-app access request.
 */
export default function ClientRealEstateNoAccess({ profile }) {
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [justSubmitted, setJustSubmitted] = useState(false);

  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!uid) {
      setRequests([]);
      setLoadingRequests(false);
      return undefined;
    }
    const q = query(collection(db, "realEstateAccessRequests"), where("userId", "==", uid), limit(10));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingRequests(false);
      },
      (e) => {
        console.error(e);
        setRequests([]);
        setLoadingRequests(false);
      }
    );
    return () => unsub();
  }, [uid]);

  const pendingRequest = useMemo(() => findPendingRealEstateAccessRequest(requests), [requests]);
  const requestComplete = justSubmitted || !!pendingRequest;

  const submitAccessRequest = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.uid) {
      setSubmitError("Sign in to request access.");
      return;
    }
    if (pendingRequest || justSubmitted) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      await addDoc(collection(db, "realEstateAccessRequests"), {
        ...buildRealEstateAccessRequestDoc({
          userId: user.uid,
          email: user.email || profile?.email || profile?.emailLower || "",
          displayName: requesterDisplayName(profile, user),
          source: RE_ACCESS_REQUEST_SOURCE,
        }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setJustSubmitted(true);
    } catch (err) {
      console.error(err);
      setSubmitError(err?.message || "Could not submit your request. Please try again or email Mark directly.");
    } finally {
      setSubmitting(false);
    }
  }, [pendingRequest, justSubmitted, profile]);

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-stone-50 via-neutral-50 to-stone-100/90">
      <div className="mx-auto max-w-xl px-4 py-12 md:py-16">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm shadow-slate-900/[0.04] md:p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Real estate portal access
          </p>
          <h1 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 md:text-2xl leading-snug">
            Buying, selling, investing, and market research — organized in one place.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            Mark&apos;s real estate portal gives clients a private place to review real estate activity, track
            transactions, save research, access market resources, and stay organized throughout a buying, selling,
            or investing journey.
          </p>
          <p className="mt-3 text-sm font-medium text-slate-800">When access is enabled, you may be able to:</p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-600">
            <li>Track active buying or selling transactions</li>
            <li>Review key documents and milestones</li>
            <li>Research Calgary neighbourhoods and condo buildings</li>
            <li>Save and compare properties</li>
            <li>Access market and investment resources</li>
            <li>Collaborate directly with Mark</li>
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            Whether you are preparing to sell, exploring your next purchase, researching condos, or evaluating an
            investment, the portal is designed to keep everything clear and accessible.
          </p>

          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div className="font-semibold text-slate-900">Mark Beattie</div>
            <div className="mt-0.5 text-slate-600">Residential Real Estate Advisor &amp; General Contractor</div>
            <ul className="mt-2 space-y-1">
              <li>
                <a
                  className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700"
                  href={`mailto:${MARK_BEATTIE_CONTACT_EMAIL}`}
                >
                  {MARK_BEATTIE_CONTACT_EMAIL}
                </a>
              </li>
              <li>
                <a
                  className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700"
                  href={`tel:${MARK_BEATTIE_CONTACT_PHONE.replace(/[^\d+]/g, "")}`}
                >
                  {MARK_BEATTIE_CONTACT_PHONE}
                </a>
              </li>
            </ul>
          </div>

          {requestComplete ? (
            <div
              className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950"
              role="status"
            >
              Access request submitted. Mark has been notified and will review your request.
            </div>
          ) : null}

          {submitError ? (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {submitError}
            </div>
          ) : null}

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              className="inline-flex items-center justify-center min-h-11 px-4 py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={requestComplete || submitting || loadingRequests}
              onClick={() => void submitAccessRequest()}
            >
              {submitting
                ? "Submitting…"
                : requestComplete
                  ? "Request submitted"
                  : "Request real estate access"}
            </button>
            <Link
              to="/client-portal?workspace=construction"
              className="inline-flex items-center justify-center min-h-11 px-4 py-2.5 text-sm font-medium border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            >
              Back to My projects
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
